import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {forkJoin, Observable, Subject} from "rxjs";
import {Centre} from "../../../../interfaces/Centres";
import {CentreService} from "../../../../services/centre.service";
import {MatTableDataSource} from "@angular/material/table";
import {map, take, takeUntil} from "rxjs/operators";
import {CounselingService} from "../../../counseling/counseling.service";
import {Employee} from "../../../../interfaces/Employee";
import {LoadingService} from "../../../../services/loading.service";
import {EmployeeService} from "../../../../services/employee.service";
import {MatSelectChange} from "@angular/material/select";
import {
  CounsellingSessionReviewService
} from "../../../counseling/components/CounsellingSessionReview/counselling-session-review.service";

export interface Counsellor  {
  profileImages: string;
  userName: string;
  designation: string;
  dateOfJoining: string;
  centre: string[];
  sessionCount: number;
  sessionObserve: number;
  individualCount: number;
  followUpCount: number;
  expanded?: boolean;
  remark?: string;
}

@Component({
  selector: 'app-mis-counselling2',
  templateUrl: './mis-counselling2.component.html',
  styleUrls: ['./mis-counselling2.component.scss']
})
export class MisCounselling2Component implements OnInit, OnChanges, OnDestroy {

  @Input() selectedCentreCodes: string[] = [];
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() isNestedComponent: boolean = false;
  @Input() isViewMode: boolean = false;

  /** Map of counsellor userName → remark, used to pre-fill remarks when loading an existing presentation */
  @Input() counsellorRemarks: { [userName: string]: string } = {};

  /** Emits the current remarks map whenever a remark is edited */
  @Output() remarksChange = new EventEmitter<{ [userName: string]: string }>();

  allUsers: Employee[] = [];
  counsellorColumns: string[] = [
    'no',
    'photo',
    'userName',
    'centre',
    'totalSessionCount',
    'SessionObservedCount',
    'remark'
  ];
  dataSource = new MatTableDataSource<Employee>([]);
  selectedCentre: string[] = ['All Centres'];
  centres$: Observable<Centre[]>;
  allowedCentres$ :Observable<Centre[]>;
  startDate_local: Date | null = null;
  endDate_local: Date | null = null;
  totalUnits = 0;

  /** Local loading flag used when isNestedComponent=true to avoid triggering the global spinner */
  isLocalLoading: boolean = false;

  counsellorsType:'Active'|'All' = 'Active'

  /** Cancels the previous counsellors subscription when a new load starts */
  private usersCancel$ = new Subject<void>();
  /** Cancels the previous forkJoin counts request when a new one starts */
  private countsCancel$ = new Subject<void>();
  /** Completes all subjects on destroy */
  private destroy$ = new Subject<void>();

  constructor(private centerService  : CentreService,
              private  counselingService: CounselingService,
              public loadingService : LoadingService,
              private employeeService: EmployeeService,
              private counsellingSessionReviewService : CounsellingSessionReviewService,
              private cdr: ChangeDetectorRef) {
    const sortByName = map((centres: Centre[]) => [...centres].sort((a, b) => (a.centreName ?? '').trim().localeCompare((b.centreName ?? '').trim())));
    this.centres$ = this.centerService.getAllCentres().pipe(sortByName)
    this.allowedCentres$ = this.centerService.getAllCentres().pipe(sortByName);
  }

  ngOnInit() {
    // Component is self-contained - user will use internal filters to search
    console.log("🔄 MIS Counselling 2 initialized - ready for user search");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isNestedComponent) return;

    const centreChange = changes['selectedCentreCodes'];
    const startChange  = changes['startDate'];
    const endChange    = changes['endDate'];

    if (!centreChange && !startChange && !endChange) return;

    // Only react when we have valid date values
    const newStart = startChange ? startChange.currentValue : this.startDate;
    const newEnd   = endChange   ? endChange.currentValue   : this.endDate;
    if (!newStart || !newEnd) return;

    // Sync inputs into internal state
    this.selectedCentre = (this.selectedCentreCodes && this.selectedCentreCodes.length > 0)
      ? [...this.selectedCentreCodes]
      : ['All Centres'];
    this.startDate_local = newStart;
    this.endDate_local   = newEnd;

    // Reset users so fresh data is fetched with the updated filters
    this.allUsers = [];
    this.dataSource.data = [];
    this.loadUsers();
  }

  onSearch() {
    if (!this.startDate_local || !this.endDate_local) {
      alert('Please select start and end dates');
      return;
    }
    if (this.allUsers.length === 0) {
      this.loadUsers();
    } else {
      this.loadAllCounts();
    }
  }

  convertFirebaseDate(value: any): Date | null {
    if (!value) return null;
    if (value.seconds) {
      return new Date(value.seconds * 1000);
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    if (typeof value === 'string') {
      return new Date(value);
    }
    return null;
  }

  private setLoading(state: boolean) {
    if (this.isNestedComponent) {
      this.isLocalLoading = state;
      this.cdr.markForCheck();
    } else {
      this.loadingService.setLoading(state);
    }
  }

  loadUsers() {
    // Cancel any previous in-flight counsellors subscription and counts request
    this.usersCancel$.next();
    this.countsCancel$.next();

    this.setLoading(true);
    console.log("📥 Loading counsellors...");

    const counsellors$ = this.counsellorsType === 'Active'
      ? this.employeeService.getCounsellers()
      : this.employeeService.getAllCounsellors();

    counsellors$.pipe(
      take(1),                      // complete after first emission — no live re-triggers
      takeUntil(this.usersCancel$)  // cancel if a new load starts before first emission
    ).subscribe({
      next: (res: Employee[]) => {
        this.allUsers = res
          .map((u: any) => ({
            profileImages: u.profileImages || 'assets/img/user-avatar.png',
            userName: u.userName || 'Unknown',
            designation: u.jobtitle || 'Counsellor',
            dateOfJoining: u.dateOfJoining || 'N/A',
            centre: u.centre ? Object.values(u.centre) : [],
            disabled: u.disabled ?? false,
            sessionCount: 0,
            sessionObserve: 0,
            individualCount: 0,
            followUpCount: 0
          } as Counsellor))
          .filter(Boolean)
          .sort((a: any, b: any) =>
            (a.userName || '').localeCompare(b.userName || '')
          ) as any[];

        const mode = this.counsellorsType === 'Active' ? '' : ' (All mode)';
        console.log(`  ✓ Loaded ${this.allUsers.length} counsellors${mode}`);

        this.removeInactiveCentres(this.allUsers);
        this.moveKrishaAndVaishaliToTop();
        this.applyRemarks();
        this.dataSource.data = [...this.allUsers];

        console.log("  ✓ Loading sessions with current filters...");
        this.loadAllCounts();
      },
      error: (err) => {
        console.error('Error loading counsellors:', err);
        this.setLoading(false);
      }
    });
  }

  getPhoto(img?: string): string {
    return img && img.trim() !== '' ? img : 'assets/images/userbig.png';
  }

  onImageError(event: any) {
    event.target.src = 'assets/images/userbig.png';
  }

  toggleCentre(row: Counsellor) {
    row.expanded = !row.expanded;
  }

  /** Returns true when the session's centre matches the current multi-selection. */
  private isCentreMatch(sessionCentre: string): boolean {
    if (!this.selectedCentre || this.selectedCentre.length === 0) return true;
    if (this.selectedCentre.includes('All Centres')) return true;
    return this.selectedCentre.includes(sessionCentre);
  }

  loadAllCounts() {
    // Cancel any previous in-flight forkJoin before starting a new one
    this.countsCancel$.next();
    this.setLoading(true);

    forkJoin({
      groupSessions:      this.counselingService.fetchGroupSessions().pipe(take(1)),
      individualSessions: this.counselingService.fetchIndividualSession().pipe(take(1)),
      followUpSessions:   this.counselingService.fetchFollowUpSession().pipe(take(1)),
      reviews:            this.counsellingSessionReviewService.getAll().pipe(take(1))
    }).pipe(
      takeUntil(this.countsCancel$)  // auto-cancel if a newer request starts
    ).subscribe({
      next: ({ groupSessions, individualSessions, followUpSessions, reviews }) => {

        // Reset all counts atomically before recounting
        this.allUsers.forEach((u: any) => {
          u.sessionCount   = 0;
          u.individualCount = 0;
          u.followUpCount  = 0;
          u.sessionObserve = 0;
        });

        // Group Sessions
        (groupSessions as any[]).forEach((session: any) => {
          if (!this.isWithinDateRange(session)) return;
          if (!this.isCentreMatch(session.centreCode || session.centre)) return;
          const counsellor: any = this.allUsers.find(
            (u: any) => u.userName?.toLowerCase() === session.submittedBy?.toLowerCase()
          );
          if (counsellor) counsellor.sessionCount++;
        });

        // Individual Sessions
        (individualSessions as any[]).forEach((session: any) => {
          if (!this.isWithinDateRange(session)) return;
          if (!this.isCentreMatch(session.centreCode || session.centre)) return;
          const counsellor: any = this.allUsers.find(
            (u: any) => u.userName?.toLowerCase() === session.submittedBy?.toLowerCase()
          );
          if (counsellor) counsellor.individualCount++;
        });

        // Follow Up Sessions
        (followUpSessions as any[]).forEach((session: any) => {
          if (!this.isWithinDateRange(session)) return;
          if (!this.isCentreMatch(session.centreCode || session.centre)) return;
          const counsellor: any = this.allUsers.find(
            (u: any) => u.userName?.toLowerCase() === session.submittedBy?.toLowerCase()
          );
          if (counsellor) counsellor.followUpCount++;
        });

        // Session Observed — only Krisha Shah and Vaishali Nanda are observers.
        // Count how many sessions each of them observed within the date range.
        const observerNames = ['krisha shah', 'vaishali nanda'];
        (reviews as any[]).forEach((review: any) => {
          const observedBy = review.observedBy;
          if (!observedBy) return;

          // Date range filter using sessionDate (observations don't have a `date` field)
          if (review.sessionDate) {
            const reviewDate = this.convertFirebaseDate(review.sessionDate);
            if (reviewDate && this.startDate_local && this.endDate_local) {
              reviewDate.setHours(0, 0, 0, 0);
              const start = new Date(this.startDate_local);
              start.setHours(0, 0, 0, 0);
              const end = new Date(this.endDate_local);
              end.setHours(23, 59, 59, 999);
              if (reviewDate < start || reviewDate > end) return;
            }
          }

          // Find the observer (Krisha Shah / Vaishali Nanda) in the users list
          const observer: any = this.allUsers.find(
            (u: any) => observerNames.includes(u.userName?.toLowerCase())
              && u.userName?.toLowerCase().includes(observedBy.toLowerCase())
          );
          if (observer) observer.sessionObserve++;
        });

        this.dataSource.data = [...this.allUsers];
      },
      error: (err) => {
        console.error('Error loading counts:', err);
        this.setLoading(false);
      },
      complete: () => {
        this.setLoading(false);
      }
    });
  }

  isWithinDateRange(session: any): boolean {
    const startDate = this.startDate_local;
    const endDate = this.endDate_local;

    if (!startDate || !endDate) return true;
    const sessionDate = this.convertFirebaseDate(session.date);
    if (!sessionDate) return false;
    sessionDate.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return sessionDate >= start && sessionDate <= end;
  }


  formatZero(value: number): string | number {
    return value === 0 ? '-' : value;
  }

  /** Called when a remark input changes – updates the map and emits it to the parent */
  onRemarkChange(userName: string, remark: string): void {
    this.counsellorRemarks = { ...this.counsellorRemarks, [userName]: remark };
    this.remarksChange.emit(this.counsellorRemarks);
  }

  /** Apply saved remarks from the counsellorRemarks map onto the loaded user rows */
  private applyRemarks(): void {
    if (!this.counsellorRemarks || Object.keys(this.counsellorRemarks).length === 0) return;
    this.allUsers.forEach((u: any) => {
      if (this.counsellorRemarks[u.userName]) {
        u.remark = this.counsellorRemarks[u.userName];
      }
    });
  }

  protected onCounsellorTypeChange($event: MatSelectChange) {
    this.counsellorsType = $event.value;
  }

  private removeInactiveCentres(allUsers: Employee[]) {
    const centresToRemove = ['ALBandra', 'ALGhatla', 'ALAndheri', 'ALThane'];
    allUsers.forEach((user: any) => {
      if (user.centre && Array.isArray(user.centre)) {
        user.centre = user.centre.filter((centre: string) => !centresToRemove.includes(centre));
      }
    });
    this.dataSource.data = [...allUsers];
  }

  private moveKrishaAndVaishaliToTop() {
    const vaishaliIndex = this.allUsers.findIndex((user: any) => user.userName === 'Vaishali Nanda');
    if (vaishaliIndex > -1) {
      const vaishali = this.allUsers.splice(vaishaliIndex, 1)[0];
      this.allUsers.unshift(vaishali);
    }
    const krishaIndex = this.allUsers.findIndex((user: any) => user.userName === 'Krisha Shah');
    if (krishaIndex > -1) {
      const krisha = this.allUsers.splice(krishaIndex, 1)[0];
      this.allUsers.unshift(krisha);
    }
  }

  ngOnDestroy(): void {
    this.usersCancel$.next();
    this.usersCancel$.complete();
    this.countsCancel$.next();
    this.countsCancel$.complete();

    this.destroy$.next();
    this.destroy$.complete();
  }
}

